import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import { Add, Delete, Edit, Refresh } from '@mui/icons-material';
import api from '../services/api';
import type { Insumo, InsumoListItem, Material, Service } from '../types';
import { useDebounce } from '../hooks/useDebounce';
import TextField from '../components/common/TextField';

interface RecipeItemForm {
  componentMaterialId?: number;
  componentServiceId?: number;
  quantity: number;
  unit?: string;
}

export const InsumosPage: React.FC = () => {
  const [insumos, setInsumos] = useState<InsumoListItem[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Insumo | null>(null);
  const [form, setForm] = useState<{ name: string; code: string; unit: string; currentStock: number; minStock: number; isActive: boolean }>({
    name: '',
    code: '',
    unit: '',
    currentStock: 0,
    minStock: 0,
    isActive: true,
  });
  const [recipeItems, setRecipeItems] = useState<RecipeItemForm[]>([]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getInsumos({ page, pageSize, search, isActive: true });
      setInsumos(res.items || []);
      setTotalPages(Math.ceil((res.total || 0) / pageSize) || 1);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadMaterialsAndServices = async () => {
    try {
      const [matRes, srvRes] = await Promise.all([
        api.getMaterials({ page: 1, pageSize: 1000 }),
        api.getServices({ page: 1, pageSize: 1000 }),
      ]);
      setMaterials(matRes.materials || []);
      setServices(srvRes.services || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadMaterialsAndServices();
  }, []);

  const debouncedSearch = useDebounce(search, 300);
  const isFirstRender = useRef(true);

  useEffect(() => {
    load();
  }, [page, pageSize]);

  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    setPage(1);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const onSearch = () => {
    setPage(1);
    load();
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', code: '', unit: '', currentStock: 0, minStock: 0, isActive: true });
    setRecipeItems([]);
    setOpen(true);
  };

  const openEdit = async (item: InsumoListItem) => {
    try {
      const full = await api.getInsumo(item.id);
      setEditing(full);
      setForm({
        name: full.name,
        code: full.code || '',
        unit: full.unit || '',
        currentStock: full.currentStock || 0,
        minStock: full.minStock || 0,
        isActive: full.isActive,
      });
      setRecipeItems(
        full.recipeItems.map((ri) => ({
          componentMaterialId: ri.componentMaterialId,
          componentServiceId: ri.componentServiceId,
          quantity: ri.quantity,
          unit: ri.unit,
        }))
      );
      setOpen(true);
    } catch (e) {
      console.error(e);
    }
  };

  const save = async () => {
    if (!form.name) return;
    try {
      if (editing) {
        await api.updateInsumo(editing.id, {
          name: form.name,
          code: form.code || undefined,
          unit: form.unit || undefined,
          currentStock: form.currentStock,
          minStock: form.minStock,
          isActive: form.isActive,
          recipeItems: recipeItems.filter((r) => r.componentMaterialId || r.componentServiceId),
        });
      } else {
        await api.createInsumo({
          name: form.name,
          code: form.code || undefined,
          unit: form.unit || undefined,
          currentStock: form.currentStock,
          minStock: form.minStock,
          isActive: form.isActive,
          recipeItems: recipeItems.filter((r) => r.componentMaterialId || r.componentServiceId),
        });
      }
      setOpen(false);
      load();
    } catch (e) {
      console.error(e);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('¿Eliminar este insumo?')) return;
    await api.deleteInsumo(id);
    load();
  };

  const addRecipeItem = () => {
    setRecipeItems([...recipeItems, { quantity: 1 }]);
  };

  const updateRecipeItem = (idx: number, field: string, value: any) => {
    const updated = [...recipeItems];
    (updated[idx] as any)[field] = value;
    // Clear the other component type when selecting
    if (field === 'componentMaterialId' && value) {
      updated[idx].componentServiceId = undefined;
    } else if (field === 'componentServiceId' && value) {
      updated[idx].componentMaterialId = undefined;
    }
    setRecipeItems(updated);
  };

  const removeRecipeItem = (idx: number) => {
    setRecipeItems(recipeItems.filter((_, i) => i !== idx));
  };

  const calculateCost = (): number => {
    let total = 0;
    for (const item of recipeItems) {
      let unitCost = 0;
      if (item.componentMaterialId) {
        const mat = materials.find((m) => m.id === item.componentMaterialId);
        unitCost = mat?.cost || 0;
      } else if (item.componentServiceId) {
        const srv = services.find((s) => s.id === item.componentServiceId);
        unitCost = srv?.cost || 0;
      }
      total += unitCost * (item.quantity || 0);
    }
    return total;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h4" fontWeight={700}>
          Insumos
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Nuevo insumo
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar por nombre o codigo"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSearch()}
        />
        <Button variant="outlined" onClick={onSearch}>
          Buscar
        </Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Codigo</TableCell>
              <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Unidad</TableCell>
              <TableCell align="right">Stock</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Stock Min</TableCell>
              <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Costo</TableCell>
              <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Componentes</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {insumos.map((insumo) => (
              <TableRow key={insumo.id} hover>
                <TableCell>{insumo.name}</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{insumo.code || '-'}</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{insumo.unit || '-'}</TableCell>
                <TableCell align="right">
                  <Chip
                    label={Number(insumo.currentStock || 0).toLocaleString()}
                    size="small"
                    color={insumo.currentStock <= insumo.minStock ? 'warning' : 'default'}
                  />
                </TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{Number(insumo.minStock || 0).toLocaleString()}</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>${Number(insumo.cost || 0).toFixed(2)}</TableCell>
                <TableCell align="center" sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  <Chip label={insumo.recipeItemCount} size="small" />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => openEdit(insumo)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton size="small" onClick={() => remove(insumo.id)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {insumos.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No hay insumos
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} />
        </Stack>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Editar Insumo' : 'Nuevo Insumo'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Nombre"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                fullWidth
                required
              />
              <TextField
                label="Codigo"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                sx={{ width: { xs: '100%', sm: 150 } }}
              />
              <TextField
                label="Unidad"
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                sx={{ width: { xs: '100%', sm: 100 } }}
                placeholder="u, kg, m..."
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Stock actual"
                type="number"
                value={form.currentStock}
                onChange={(e) => setForm({ ...form, currentStock: Number(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ width: { xs: '100%', sm: 150 } }}
              />
              <TextField
                label="Stock minimo"
                type="number"
                value={form.minStock}
                onChange={(e) => setForm({ ...form, minStock: Number(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ width: { xs: '100%', sm: 150 } }}
                helperText="Alerta cuando stock < min"
              />
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" fontWeight={600}>
                Formulación (Componentes)
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  Costo calculado: <strong>${calculateCost().toFixed(2)}</strong>
                </Typography>
                <Button size="small" startIcon={<Add />} onClick={addRecipeItem}>
                  Agregar componente
                </Button>
              </Stack>
            </Stack>

            {recipeItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No hay componentes. Agrega materiales o servicios para definir la formulación.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Componente</TableCell>
                    <TableCell sx={{ width: 100 }}>Cantidad</TableCell>
                    <TableCell sx={{ width: 80 }}>Unidad</TableCell>
                    <TableCell align="right">Costo unit.</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                    <TableCell sx={{ width: 50 }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recipeItems.map((item, idx) => {
                    const isMaterial = !!item.componentMaterialId;
                    const isService = !!item.componentServiceId;
                    let componentCost = 0;
                    if (item.componentMaterialId) {
                      componentCost = materials.find((m) => m.id === item.componentMaterialId)?.cost || 0;
                    } else if (item.componentServiceId) {
                      componentCost = services.find((s) => s.id === item.componentServiceId)?.cost || 0;
                    }

                    return (
                      <TableRow key={idx}>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={isMaterial ? 'material' : isService ? 'servicio' : ''}
                            onChange={(e) => {
                              if (e.target.value === 'material') {
                                updateRecipeItem(idx, 'componentServiceId', undefined);
                              } else {
                                updateRecipeItem(idx, 'componentMaterialId', undefined);
                              }
                            }}
                            sx={{ width: 100 }}
                          >
                            <MenuItem value="material">Material</MenuItem>
                            <MenuItem value="servicio">Servicio</MenuItem>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          {isMaterial || (!isMaterial && !isService) ? (
                            <TextField
                              select
                              size="small"
                              value={item.componentMaterialId || ''}
                              onChange={(e) => updateRecipeItem(idx, 'componentMaterialId', e.target.value ? Number(e.target.value) : undefined)}
                              fullWidth
                              disabled={isService}
                            >
                              <MenuItem value="">Seleccionar material</MenuItem>
                              {materials.map((m) => (
                                <MenuItem key={m.id} value={m.id}>
                                  {m.name} {m.code ? `(${m.code})` : ''}
                                </MenuItem>
                              ))}
                            </TextField>
                          ) : (
                            <TextField
                              select
                              size="small"
                              value={item.componentServiceId || ''}
                              onChange={(e) => updateRecipeItem(idx, 'componentServiceId', e.target.value ? Number(e.target.value) : undefined)}
                              fullWidth
                            >
                              <MenuItem value="">Seleccionar servicio</MenuItem>
                              {services.map((s) => (
                                <MenuItem key={s.id} value={s.id}>
                                  {s.name} {s.code ? `(${s.code})` : ''}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={item.quantity || ''}
                            onChange={(e) => updateRecipeItem(idx, 'quantity', Number(e.target.value))}
                            inputProps={{ min: 0, step: 0.01 }}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={item.unit || ''}
                            onChange={(e) => updateRecipeItem(idx, 'unit', e.target.value)}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="right">${componentCost.toFixed(2)}</TableCell>
                        <TableCell align="right">${(componentCost * (item.quantity || 0)).toFixed(2)}</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => removeRecipeItem(idx)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={!form.name}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InsumosPage;
